import React, { useEffect, useState } from 'react'
import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table'
import { mdiBinoculars, mdiDelete, mdiLoading, mdiSortAlphabeticalAscending, mdiSortAlphabeticalDescending } from '@mdi/js'
import Icon from '@mdi/react'
import GlobalFilter from './GlobalFilter'
import { useRouter } from '@tanstack/react-router'
import { Modal } from 'react-bootstrap'
import DeleteSurveyModal from '../modals/DeleteSurveyModal'
import { api } from '../shared/ApiProvider'

// Let's add a fetchData method to our Table component that will be used to fetch
// new data when pagination state changes
// We can also add a loading state to let our table know it's loading new data
const SurveyList = ({ setShowSurveyUpdateModal, columns, data, totalResults, setSortField, setSortOrder, sortOrder, sortField, loading, setSurveyInitialVals }) => {
  const router = useRouter()
  const [globalFilter, setGlobalFilter] = useState('')
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [noPermLoading, setNoPermLoading] = useState(false)
  const [showDeleteSurveyModal, setShowDeleteSurveyModal] = useState(false)
  const [deleteSurveyId, setDeleteSurveyId] = useState(null)
  const [showIcons, setShowIcons] = useState({})

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter,
      pagination: { pageIndex, pageSize },
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: updater => {
      const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater
      setPageIndex(next.pageIndex)
      setPageSize(next.pageSize)
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(totalResults / pageSize) || 1, // Use totalResults for proper page count
  })

  // Methods
  const handleRowClick = async (event, rowData = null) => {
    try {
      // When called from row click (not button), extract from event target
      let responseId, permNumber, permNumberVerified

      if (rowData) {
        // Called from button with explicit rowData
        responseId = rowData.responseId
        permNumber = rowData.permNumber
        permNumberVerified = rowData.permNumberVerified
      } else {
        // Called from row click - extract from event
        const rowElement = event?.currentTarget?.closest('[data-rowid]') || event?.currentTarget
        if (!rowElement) {
          console.error('No row element found')
          return
        }
        responseId = rowElement.dataset.id_ref
        permNumber = rowElement.dataset.perm
        permNumberVerified = false
      }

      console.log(`handleRowClick - responseId: ${responseId} permNumber: ${permNumber}`)

      if (!responseId) {
        console.error('No response ID found')
        return
      }

      if (permNumberVerified) {
        localStorage.setItem('yCoord', window.scrollY.toString())
        router.navigate({ to: `/form/${responseId}` })
      } else {
        // Set modal edit for updating survey, then upon close, update and research the database.
        setNoPermLoading(true)
        try {
          const studentData = await api.get(`/survey/${responseId}`)
          setSurveyInitialVals({ ...studentData.data, id: responseId })
          setShowSurveyUpdateModal(true)
        } catch (error) {
          console.error('Error fetching student data:', error)
        } finally {
          setNoPermLoading(false)
        }
      }
    } catch (error) {
      console.error('Error in handleRowClick:', error)
    }
    event.preventDefault()
  }

  const handleSetSortField = event => {
    event.preventDefault()
    const field = event.currentTarget.dataset.id_ref
    if (!field) return

    const sortFieldToUse = field === 'dob' ? 'birthDate' : field
    setSortField(sortFieldToUse)
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
  }

  const showRowIcons = e => {
    const rowElement = e.currentTarget
    const id = rowElement.dataset.rowid
    if (id) {
      setShowIcons(prev => ({ ...prev, [id]: true }))
    }
  }

  const hideRowIcons = e => {
    const rowElement = e.currentTarget
    const id = rowElement.dataset.rowid
    if (id) {
      setShowIcons(prev => ({ ...prev, [id]: false }))
    }
  }

  const deleteSurvey = e => {
    e.stopPropagation() // Prevent row click
    const responseId = e.currentTarget.dataset.id_ref
    if (responseId) {
      setDeleteSurveyId(responseId)
      setShowDeleteSurveyModal(true)
    }
  }

  const handleViewClick = async (e, row) => {
    e.stopPropagation() // Prevent event bubbling

    const responseId = row.original?.responseId
    const permNumber = row.original?.permNumber
    const permNumberVerified = row.original?.permNumberVerified

    if (!responseId) {
      console.error('No response ID found')
      return
    }

    // Check if survey has a verified PERM number
    // Use != null to allow 0 as a valid perm number
    if (permNumber != null && permNumberVerified === true) {
      // Has verified PERM number - navigate to side-by-side layout
      localStorage.setItem('yCoord', window.scrollY.toString())
      router.navigate({ to: `/survey/${responseId}` })
    } else {
      // No PERM number or unverified - show the SurveyUpdateModal
      setNoPermLoading(true)
      try {
        const studentData = await api.get(`/survey/${responseId}`)
        setSurveyInitialVals({ ...studentData.data, id: responseId })
        setShowSurveyUpdateModal(true)
      } catch (error) {
        console.error('Error fetching student data:', error)
      } finally {
        setNoPermLoading(false)
      }
    }
  }

  // Clear icons when data changes (new page, sort, etc.)
  useEffect(() => {
    setShowIcons({})
  }, [data])

  // Render the UI for your table
  return (
    <>
      <Modal centered show={noPermLoading}>
        <Modal.Body>
          <div className="d-flex justify-content-center align-items-center">
            <Icon path={mdiLoading} spin color="blue" size={1.5} className="me-2" />
            <h5 className="mb-0">Loading...</h5>
          </div>
        </Modal.Body>
      </Modal>

      <h5 className="text-center mb-0 p-2">Home Language Surveys</h5>
      <hr />
      <div className="alert alert-info">Select a survey from the table below to compare to student on file.</div>

      <div className="container m-0 p-0 row">
        <GlobalFilter totalResults={totalResults} globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
      </div>

      <div style={{ position: 'relative', minHeight: '200px' }}>
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(255,255,255,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}>
            <div className="d-flex flex-column align-items-center">
              <Icon path={mdiLoading} spin color="blue" size={3} />
              <div className="mt-2 text-muted">Loading surveys...</div>
            </div>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-hover">
            <thead className="thead-light">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id} onClick={handleSetSortField} data-id_ref={header.id} className="bg-light text-dark user-select-none" style={{ cursor: 'pointer' }}>
                      <div className="d-flex align-items-center justify-content-between">
                        <span>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</span>
                        {sortField === header.id && <Icon size={0.8} color="orange" path={sortOrder === 'asc' ? mdiSortAlphabeticalAscending : mdiSortAlphabeticalDescending} />}
                      </div>
                    </th>
                  ))}
                  <th className="bg-secondary text-white">Actions</th>
                </tr>
              ))}
            </thead>
            <tbody>
              {data.length === 0 && !loading ? (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center py-4 text-muted">
                    No surveys found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    onMouseEnter={showRowIcons}
                    onMouseLeave={hideRowIcons}
                    className="align-items-center"
                    style={{ cursor: 'pointer' }}
                    data-rowid={row.id}
                    data-perm={row.original?.permNumber || ''}
                    data-id_ref={row.original?.responseId}
                    data-first={row.original?.firstName}
                    onClick={handleRowClick}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="align-middle">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                    <td className="align-middle">
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={e => handleViewClick(e, row)}
                          data-rowid={row.id}
                          style={{
                            opacity: showIcons[row.id] ? 1 : 0.3,
                            transition: 'opacity 0.2s ease',
                          }}
                          title="View Survey">
                          <Icon path={mdiBinoculars} size={0.7} />
                          <span className="ms-1 d-none d-sm-inline">View</span>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={deleteSurvey}
                          data-id_ref={row.original?.responseId}
                          data-rowid={row.id}
                          style={{
                            opacity: showIcons[row.id] ? 1 : 0.3,
                            transition: 'opacity 0.2s ease',
                          }}
                          title="Delete Survey">
                          <Icon path={mdiDelete} size={0.7} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteSurveyModal showDeleteSurveyModal={showDeleteSurveyModal} setShowDeleteSurveyModal={setShowDeleteSurveyModal} responseId={deleteSurveyId} />
    </>
  )
}

export default SurveyList
